import { Alert, AlertTitle, Collapse, Typography } from '@mui/material';
import { HTMLAttributes } from 'react';
import { useForm } from 'react-hook-form';
import { ActionButton } from '../../../reusable/action-button';
import {
  AssessmentCard,
  AssessmentHeader,
  ControlledSelectField,
} from '../../shared';

export type StateEntry = {
  value: string;
  label: string;
};

type StateCheckScreenProps = {
  patientPreferredName?: string;
  patientAddressState?: string;
  allStates?: StateEntry[];
  supportedStateCodes?: string[];

  onContinue?: () => void;
} & Partial<HTMLAttributes<HTMLDivElement>>;

const StateCheckScreen = (props: StateCheckScreenProps) => {
  const { control, watch } = useForm();
  const selectedStateCode = watch('state');
  const isSupported = props.supportedStateCodes
    ? props.supportedStateCodes.includes(selectedStateCode)
    : false;
  const isNotSupported = selectedStateCode && !isSupported;

  return (
    <div {...props}>
      <AssessmentHeader title="Let's see if we have clinicians licensed in your state" />
      <AssessmentCard>
        <Typography component="h3" variant="h5">
          Where do you live, {props.patientPreferredName ?? ''}?
        </Typography>
        <Typography variant="body1" component="p" sx={{ pt: 1 }}>
          Even for online visits, we have to make sure your clinician is
          licensed to practice medicine in your state.
        </Typography>
        <ControlledSelectField<string>
          name="state"
          control={control}
          defaultValue={props.patientAddressState ?? undefined}
          options={(props.allStates ?? []).map(option => ({
            label: option.label,
            value: option.value,
          }))}
          label="State"
          sx={{ mt: 2 }}
        />
        <Collapse in={isSupported} unmountOnExit>
          <Alert severity="success" sx={{ mt: 2 }}>
            Great! We have providers licensed in{' '}
            {selectedStateCode &&
              props.allStates.find(s => s.value === selectedStateCode).label}
          </Alert>
        </Collapse>
        <Collapse in={isNotSupported} unmountOnExit>
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>We're not in your state yet</AlertTitle>
            But we're always working to expand our practice to cover more of the
            country. Please check back with us soon.
          </Alert>
        </Collapse>
      </AssessmentCard>
      {!isNotSupported && (
        <ActionButton disabled={!isSupported} onClick={props.onContinue}>
          Enter your insurance information
        </ActionButton>
      )}
      {isNotSupported && (
        <ActionButton secondary href="https://www.talkiatry.com/">
          Return to Talkiatry.com
        </ActionButton>
      )}
    </div>
  );
};

export default StateCheckScreen;
